// BACKEND INTEGRATION POINT: Replace with API calls to Supabase backend

export type NotificationStatus = 'unread' | 'seen' | 'pending' | 'deferred' | 'calling' | 'handled' | 'failed';
export type CallStatus = 'ended' | 'missed' | 'declined' | 'cancelled';
export type CallType = 'video' | 'audio';
export type MessageType = 'text' | 'voice' | 'photo' | 'call' | 'notification';
export type ActivityCategory = 'call' | 'message' | 'photo' | 'radio' | 'agenda' | 'quiz' | 'connection' | 'callback';
export type ActivityStatus = 'new' | 'seen' | 'pending' | 'deferred' | 'calling' | 'handled' | 'failed' | 'informational';
export type ContactStatus = 'admin' | 'authorized' | 'secondary' | 'pending_invite';

export interface ContactPermissions {
  canCall: boolean;
  canReceiveCallbacks: boolean;
  canSendMessages: boolean;
  canSendPhotos: boolean;
  canEditAgenda: boolean;
  canReceiveConnectionAlerts: boolean;
  canViewActivities: boolean;
  canEditSettings: boolean;
}

export interface FamilyContact {
  id: string;
  firstName: string;
  lastName: string;
  relationship: string;
  phone: string;
  email: string;
  avatarUrl: string;
  status: ContactStatus;
  isActive: boolean;
  isAdmin: boolean;
  permissions: ContactPermissions;
}

export interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  relationToSenior: string;
  relationFromSenior: string;
  familyRole: string;
  priority: number;
  avatarUrl: string;
  permissions: {
    canCall: boolean;
    canMessage: boolean;
    canSendPhoto: boolean;
    canEditAgenda: boolean;
    canReceiveAlerts: boolean;
    canInvite: boolean;
  };
}

export interface SeniorProfile {
  id: string;
  firstName: string;
  lastName: string;
  photoUrl: string;
  connectionStatus: string;
  socialStatus: string;
  lastSeenAt: string;
  lastCallAt?: string;
  lastCallDuration?: string;
}

export interface FamilyNotification {
  id: string;
  type: 'callback_request' | 'missed_call' | 'unread_message' | 'tablet_offline' | 'photo_unseen' | 'no_activity';
  seniorId: string;
  contactId: string | null;
  contentId: string | null;
  occurredAt: string;
  status: NotificationStatus;
  title: string;
  description: string;
  actionLabel: string;
}

export interface Message {
  id: string;
  contactId: string;
  seniorId: string;
  type: MessageType;
  content: string;
  sentAt: string;
  readStatus: 'sent' | 'received' | 'seen' | 'listened' | 'handled';
  direction: 'outgoing' | 'incoming';
  thumbnailUrl?: string;
  mediaUrl?: string;
  durationSeconds?: number;
  callType?: CallType;
  callStatus?: CallStatus;
  notifId?: string;
}

export interface AgendaEvent {
  id: string;
  type: 'visit' | 'call' | 'family' | 'activity' | 'reminder';
  contactId: string | null;
  seniorId: string;
  date: string;
  time: string;
  label: string;
  note?: string;
  color: string;
  isoDate?: string; // YYYY-MM-DD for calendar
  reminder?: boolean;
}

export interface CallRecord {
  id: string;
  seniorId: string;
  contactId: string;
  type: CallType;
  status: CallStatus;
  startedAt: string;
  endedAt: string | null;
  durationSeconds: number | null;
  direction: 'outgoing' | 'incoming';
  sourceNotificationId: string | null;
}

export interface FamilyActivity {
  id: string;
  type: 'call' | 'message' | 'photo' | 'view' | 'visit' | 'callback' | 'quiz';
  contactId: string | null;
  seniorId: string;
  occurredAt: string;
  label: string;
  important: boolean;
}

export interface RecentActivity {
  id: string;
  seniorId: string;
  contactId?: string;
  category: ActivityCategory;
  action: string;
  label: string;
  description?: string;
  createdAt: string;
  relatedEntityId?: string;
  status: ActivityStatus;
  durationSeconds?: number;
  metadata?: Record<string, string | number | boolean>;
}

export interface DemoState {
  seniorProfile: SeniorProfile;
  contactsById: Record<string, Contact>;
  messagesByContact: Record<string, Message[]>;
  familyNotifications: FamilyNotification[];
  familyActivities: FamilyActivity[];
  agendaEvents: AgendaEvent[];
  callHistory: CallRecord[];
  recentActivities: RecentActivity[];
  familyContacts: FamilyContact[];
}

// Single canonical André portrait used on every screen
export const ANDRE_PHOTO_URL = 'https://img.rocket.new/generatedImages/rocket_gen_img_1bc93b3ef-1767526138775.png';
// Marie avatar
export const MARIE_AVATAR_URL = 'https://img.rocket.new/generatedImages/rocket_gen_img_1645d3016-1773176995009.png';

const INITIAL_STATE: DemoState = {
  seniorProfile: {
    id: 'andre',
    firstName: 'André',
    lastName: 'Beaumont',
    photoUrl: ANDRE_PHOTO_URL,
    connectionStatus: 'Connecté il y a quelques minutes',
    socialStatus: 'Tout semble normal aujourd\'hui',
    lastSeenAt: '2026-07-28T13:55:00-04:00',
    lastCallAt: '2026-07-27T18:20:00-04:00',
    lastCallDuration: '12 minutes',
  },

  contactsById: {
    marie: {
      id: 'marie',
      firstName: 'Marie',
      lastName: 'Beaumont',
      relationToSenior: 'Fille d\'André',
      relationFromSenior: 'Ma fille',
      familyRole: 'Proche principal',
      priority: 1,
      avatarUrl: MARIE_AVATAR_URL,
      permissions: {
        canCall: true,
        canMessage: true,
        canSendPhoto: true,
        canEditAgenda: true,
        canReceiveAlerts: true,
        canInvite: true
      }
    },
    jean: {
      id: 'jean',
      firstName: 'Jean',
      lastName: 'Beaumont',
      relationToSenior: 'Fils d\'André',
      relationFromSenior: 'Mon fils',
      familyRole: 'Proche famille',
      priority: 2,
      avatarUrl: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200&h=200&fit=crop&crop=face',
      permissions: {
        canCall: true,
        canMessage: true,
        canSendPhoto: true,
        canEditAgenda: false,
        canReceiveAlerts: true,
        canInvite: false
      }
    },
    sophie: {
      id: 'sophie',
      firstName: 'Sophie',
      lastName: 'Beaumont',
      relationToSenior: 'Petite-fille d\'André',
      relationFromSenior: 'Ma petite-fille',
      familyRole: 'Proche famille',
      priority: 3,
      avatarUrl: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=200&h=200&fit=crop&crop=face',
      permissions: {
        canCall: true,
        canMessage: true,
        canSendPhoto: true,
        canEditAgenda: false,
        canReceiveAlerts: false,
        canInvite: false
      }
    },
    paul: {
      id: 'paul',
      firstName: 'Paul',
      lastName: 'Beaumont',
      relationToSenior: 'Frère d\'André',
      relationFromSenior: 'Mon frère',
      familyRole: 'Proche famille',
      priority: 4,
      avatarUrl: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop&crop=face',
      permissions: {
        canCall: true,
        canMessage: true,
        canSendPhoto: false,
        canEditAgenda: false,
        canReceiveAlerts: false,
        canInvite: false
      }
    },
    claire: {
      id: 'claire',
      firstName: 'Claire',
      lastName: 'Moreau',
      relationToSenior: 'Amie d\'André',
      relationFromSenior: 'Mon amie',
      familyRole: 'Ami proche',
      priority: 5,
      avatarUrl: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&h=200&fit=crop&crop=face',
      permissions: {
        canCall: true,
        canMessage: true,
        canSendPhoto: false,
        canEditAgenda: false,
        canReceiveAlerts: false,
        canInvite: false
      }
    }
  },

  messagesByContact: {
    andre: [
      // ── Hier ──
      {
        id: 'msg-call-001',
        contactId: 'marie',
        seniorId: 'andre',
        type: 'call',
        content: 'Appel vidéo — 12 minutes',
        sentAt: '2026-07-27T18:20:00-04:00',
        readStatus: 'seen',
        direction: 'outgoing',
        callType: 'video',
        callStatus: 'ended',
        durationSeconds: 720,
      },
      {
        id: 'msg-003',
        contactId: 'jean',
        seniorId: 'andre',
        type: 'voice',
        content: 'Message vocal de Jean — 0:42',
        sentAt: '2026-07-27T18:20:00-04:00',
        readStatus: 'listened',
        direction: 'outgoing',
        durationSeconds: 42,
      },
      {
        id: 'msg-notif-listened',
        contactId: 'andre',
        seniorId: 'andre',
        type: 'notification',
        content: 'André a écouté votre message vocal',
        sentAt: '2026-07-27T18:25:00-04:00',
        readStatus: 'seen',
        direction: 'incoming',
      },
      {
        id: 'msg-call-missed',
        contactId: 'andre',
        seniorId: 'andre',
        type: 'call',
        content: 'Appel manqué d\'André',
        sentAt: '2026-07-27T09:15:00-04:00',
        readStatus: 'seen',
        direction: 'incoming',
        callType: 'video',
        callStatus: 'missed',
      },
      // ── Aujourd'hui ──
      {
        id: 'msg-notif-callback',
        contactId: 'andre',
        seniorId: 'andre',
        type: 'notification',
        content: 'André souhaite être appelé',
        sentAt: '2026-07-28T14:20:00-04:00',
        readStatus: 'sent',
        direction: 'incoming',
        notifId: 'notif-callback-001',
      },
      {
        id: 'msg-001',
        contactId: 'marie',
        seniorId: 'andre',
        type: 'photo',
        content: 'Regardez ce coucher de soleil depuis la terrasse ! 🌅',
        sentAt: '2026-07-28T14:32:00-04:00',
        readStatus: 'seen',
        direction: 'outgoing',
        thumbnailUrl: 'https://img.rocket.new/generatedImages/rocket_gen_img_155d110df-1778320201227.png',
        mediaUrl: 'https://img.rocket.new/generatedImages/rocket_gen_img_155d110df-1778320201227.png',
      },
      {
        id: 'msg-notif-photo-seen',
        contactId: 'andre',
        seniorId: 'andre',
        type: 'notification',
        content: 'André a vu votre photo',
        sentAt: '2026-07-28T14:35:00-04:00',
        readStatus: 'seen',
        direction: 'incoming',
      },
      {
        id: 'msg-002',
        contactId: 'andre',
        seniorId: 'andre',
        type: 'text',
        content: 'Merci ma chérie, c\'est magnifique ! Je t\'attends pour la visite de cet après-midi.',
        sentAt: '2026-07-28T14:36:00-04:00',
        readStatus: 'seen',
        direction: 'incoming'
      },
      {
        id: 'msg-004',
        contactId: 'marie',
        seniorId: 'andre',
        type: 'text',
        content: 'Papa, j\'arrive vers 15h00 comme prévu. Tu as besoin de quelque chose ?',
        sentAt: '2026-07-28T12:10:00-04:00',
        readStatus: 'seen',
        direction: 'outgoing'
      },
      {
        id: 'msg-005',
        contactId: 'andre',
        seniorId: 'andre',
        type: 'text',
        content: 'Rien de spécial, juste te voir ! 😊',
        sentAt: '2026-07-28T12:18:00-04:00',
        readStatus: 'seen',
        direction: 'incoming'
      }
    ]
  },

  familyNotifications: [
    {
      id: 'notif-callback-001',
      type: 'callback_request',
      seniorId: 'andre',
      contactId: null,
      contentId: null,
      occurredAt: '2026-07-28T14:20:00-04:00',
      status: 'unread',
      title: 'André souhaite être appelé',
      description: 'Demande envoyée aujourd\'hui à 14h20',
      actionLabel: 'Rappeler André'
    },
    {
      id: 'notif-missed-001',
      type: 'missed_call',
      seniorId: 'andre',
      contactId: null,
      contentId: null,
      occurredAt: '2026-07-27T09:15:00-04:00',
      status: 'seen',
      title: 'Appel manqué d\'André',
      description: 'Hier à 09h15 — Appel vidéo',
      actionLabel: 'Rappeler André'
    },
    {
      id: 'notif-photo-001',
      type: 'photo_unseen',
      seniorId: 'andre',
      contactId: 'marie',
      contentId: 'msg-001',
      occurredAt: '2026-07-28T14:32:00-04:00',
      status: 'handled',
      title: 'André a vu votre photo',
      description: 'Aujourd\'hui à 14h32 — Photo du coucher de soleil',
      actionLabel: 'Voir la photo'
    },
    {
      id: 'notif-tablet-001',
      type: 'tablet_offline',
      seniorId: 'andre',
      contactId: null,
      contentId: null,
      occurredAt: '2026-07-26T22:00:00-04:00',
      status: 'handled',
      title: 'La tablette d\'André était hors ligne',
      description: 'Avant-hier soir — Connexion rétablie',
      actionLabel: 'Vérifier la connexion'
    }
  ],

  familyActivities: [
    {
      id: 'act-001',
      type: 'photo',
      contactId: 'marie',
      seniorId: 'andre',
      occurredAt: '2026-07-28T14:32:00-04:00',
      label: 'André a vu votre photo',
      important: true
    },
    {
      id: 'act-002',
      type: 'call',
      contactId: 'marie',
      seniorId: 'andre',
      occurredAt: '2026-07-28T10:15:00-04:00',
      label: 'Appel vidéo terminé avec André',
      important: true
    },
    {
      id: 'act-003',
      type: 'message',
      contactId: 'jean',
      seniorId: 'andre',
      occurredAt: '2026-07-27T18:20:00-04:00',
      label: 'André a écouté le message de Jean',
      important: false
    },
    {
      id: 'act-004',
      type: 'quiz',
      contactId: null,
      seniorId: 'andre',
      occurredAt: '2026-07-27T15:45:00-04:00',
      label: 'Quiz Martinique terminé à 65 %',
      important: false
    },
    {
      id: 'act-005',
      type: 'visit',
      contactId: 'marie',
      seniorId: 'andre',
      occurredAt: '2026-07-28T15:00:00-04:00',
      label: 'Visite de Marie prévue aujourd\'hui à 15h00',
      important: true
    },
    {
      id: 'act-006',
      type: 'callback',
      contactId: null,
      seniorId: 'andre',
      occurredAt: '2026-07-28T14:20:00-04:00',
      label: 'André a demandé à être rappelé',
      important: true
    }
  ],

  familyContacts: [
    {
      id: 'fc-marie',
      firstName: 'Marie',
      lastName: 'Beaumont',
      relationship: 'Fille',
      phone: '06 00 00 00 01',
      email: 'marie.beaumont@email.fr',
      avatarUrl: MARIE_AVATAR_URL,
      status: 'admin',
      isActive: true,
      isAdmin: true,
      permissions: {
        canCall: true,
        canReceiveCallbacks: true,
        canSendMessages: true,
        canSendPhotos: true,
        canEditAgenda: true,
        canReceiveConnectionAlerts: true,
        canViewActivities: true,
        canEditSettings: true,
      },
    },
    {
      id: 'fc-jean',
      firstName: 'Jean',
      lastName: 'Beaumont',
      relationship: 'Fils',
      phone: '06 00 00 00 02',
      email: 'jean.beaumont@email.fr',
      avatarUrl: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200&h=200&fit=crop&crop=face',
      status: 'authorized',
      isActive: true,
      isAdmin: false,
      permissions: {
        canCall: true,
        canReceiveCallbacks: true,
        canSendMessages: true,
        canSendPhotos: true,
        canEditAgenda: false,
        canReceiveConnectionAlerts: false,
        canViewActivities: true,
        canEditSettings: false,
      },
    },
    {
      id: 'fc-sophie',
      firstName: 'Sophie',
      lastName: 'Beaumont',
      relationship: 'Petite-fille',
      phone: '06 00 00 00 03',
      email: 'sophie.beaumont@email.fr',
      avatarUrl: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=200&h=200&fit=crop&crop=face',
      status: 'authorized',
      isActive: true,
      isAdmin: false,
      permissions: {
        canCall: true,
        canReceiveCallbacks: false,
        canSendMessages: true,
        canSendPhotos: true,
        canEditAgenda: false,
        canReceiveConnectionAlerts: false,
        canViewActivities: false,
        canEditSettings: false,
      },
    },
    {
      id: 'fc-paul',
      firstName: 'Paul',
      lastName: 'Beaumont',
      relationship: 'Frère',
      phone: '06 00 00 00 04',
      email: 'paul.beaumont@email.fr',
      avatarUrl: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop&crop=face',
      status: 'secondary',
      isActive: true,
      isAdmin: false,
      permissions: {
        canCall: true,
        canReceiveCallbacks: false,
        canSendMessages: false,
        canSendPhotos: false,
        canEditAgenda: false,
        canReceiveConnectionAlerts: false,
        canViewActivities: false,
        canEditSettings: false,
      },
    },
  ],

  agendaEvents: [
    {
      id: 'evt-001',
      type: 'visit',
      contactId: 'marie',
      seniorId: 'andre',
      date: 'Aujourd\'hui',
      time: '15h00',
      label: 'Visite de Marie',
      color: 'lucat-green',
      isoDate: '2026-07-28'
    },
    {
      id: 'evt-002',
      type: 'call',
      contactId: 'jean',
      seniorId: 'andre',
      date: 'Demain',
      time: '10h00',
      label: 'Appel avec Jean',
      color: 'lucat-blue',
      isoDate: '2026-07-29'
    },
    {
      id: 'evt-003',
      type: 'family',
      contactId: null,
      seniorId: 'andre',
      date: 'Samedi',
      time: '16h00',
      label: 'Goûter en famille',
      color: 'lucat-purple',
      isoDate: '2026-08-02'
    }
  ],

  callHistory: [
    {
      id: 'call-001',
      seniorId: 'andre',
      contactId: 'marie',
      type: 'video',
      status: 'ended',
      startedAt: '2026-07-27T18:20:00-04:00',
      endedAt: '2026-07-27T18:32:00-04:00',
      durationSeconds: 720,
      direction: 'outgoing',
      sourceNotificationId: null
    },
    {
      id: 'call-002',
      seniorId: 'andre',
      contactId: 'marie',
      type: 'video',
      status: 'missed',
      startedAt: '2026-07-27T09:15:00-04:00',
      endedAt: null,
      durationSeconds: null,
      direction: 'incoming',
      sourceNotificationId: null
    },
    {
      id: 'call-003',
      seniorId: 'andre',
      contactId: 'marie',
      type: 'audio',
      status: 'ended',
      startedAt: '2026-07-26T11:30:00-04:00',
      endedAt: '2026-07-26T11:38:00-04:00',
      durationSeconds: 480,
      direction: 'outgoing',
      sourceNotificationId: null
    }
  ],

  recentActivities: [
    // ── Aujourd'hui ──
    {
      id: 'ra-001',
      seniorId: 'andre',
      contactId: 'marie',
      category: 'photo',
      action: 'photo_seen',
      label: 'André a vu votre photo',
      description: 'Photo du coucher de soleil depuis la terrasse',
      createdAt: '2026-07-28T14:32:00-04:00',
      relatedEntityId: 'msg-001',
      status: 'seen',
      metadata: { mediaId: 'msg-001' },
    },
    {
      id: 'ra-002',
      seniorId: 'andre',
      contactId: 'marie',
      category: 'message',
      action: 'voice_listened',
      label: 'André a écouté votre message vocal',
      description: 'Message vocal de 42 secondes',
      createdAt: '2026-07-28T14:25:00-04:00',
      relatedEntityId: 'msg-003',
      status: 'seen',
      durationSeconds: 42,
      metadata: { messageId: 'msg-003' },
    },
    {
      id: 'ra-003',
      seniorId: 'andre',
      contactId: 'marie',
      category: 'call',
      action: 'call_ended',
      label: 'Appel vidéo terminé avec André',
      description: 'Appel vidéo sortant',
      createdAt: '2026-07-28T12:18:00-04:00',
      relatedEntityId: 'call-today-001',
      status: 'informational',
      durationSeconds: 18,
      metadata: { callId: 'call-today-001', callType: 'video', direction: 'outgoing' },
    },
    {
      id: 'ra-004',
      seniorId: 'andre',
      category: 'radio',
      action: 'radio_listened',
      label: 'André a écouté RCI',
      description: 'Radio des Caraïbes Internationales',
      createdAt: '2026-07-28T11:15:00-04:00',
      status: 'informational',
      metadata: { station: 'RCI' },
    },
    {
      id: 'ra-005',
      seniorId: 'andre',
      category: 'agenda',
      action: 'agenda_viewed',
      label: 'André a consulté sa journée',
      description: 'Consultation de l\'agenda du jour',
      createdAt: '2026-07-28T09:30:00-04:00',
      relatedEntityId: 'evt-001',
      status: 'informational',
      metadata: { eventId: 'evt-001' },
    },
    {
      id: 'ra-006',
      seniorId: 'andre',
      contactId: 'marie',
      category: 'callback',
      action: 'callback_requested',
      label: 'André a demandé à être rappelé',
      description: 'Demande de rappel envoyée',
      createdAt: '2026-07-28T14:20:00-04:00',
      relatedEntityId: 'notif-callback-001',
      status: 'new',
      metadata: { notificationId: 'notif-callback-001' },
    },
    // ── Hier ──
    {
      id: 'ra-007',
      seniorId: 'andre',
      contactId: 'marie',
      category: 'call',
      action: 'call_ended',
      label: 'Appel vidéo terminé avec André — 12 minutes',
      description: 'Appel vidéo sortant',
      createdAt: '2026-07-27T18:20:00-04:00',
      relatedEntityId: 'call-001',
      status: 'informational',
      durationSeconds: 720,
      metadata: { callId: 'call-001', callType: 'video', direction: 'outgoing' },
    },
    {
      id: 'ra-008',
      seniorId: 'andre',
      category: 'quiz',
      action: 'quiz_completed',
      label: 'Quiz Martinique terminé à 65 %',
      description: 'Quiz culturel sur la Martinique',
      createdAt: '2026-07-27T17:30:00-04:00',
      status: 'informational',
      metadata: { quizName: 'Quiz Martinique', score: 65 },
    },
    {
      id: 'ra-009',
      seniorId: 'andre',
      contactId: 'marie',
      category: 'callback',
      action: 'callback_handled',
      label: 'Demande de rappel traitée',
      description: 'André avait demandé à être rappelé — Appel effectué',
      createdAt: '2026-07-27T16:20:00-04:00',
      relatedEntityId: 'notif-callback-old',
      status: 'handled',
      metadata: { notificationId: 'notif-callback-old' },
    },
    {
      id: 'ra-010',
      seniorId: 'andre',
      category: 'connection',
      action: 'tablet_reconnected',
      label: 'La tablette d\'André s\'est reconnectée',
      description: 'Connexion rétablie après une interruption',
      createdAt: '2026-07-27T08:45:00-04:00',
      status: 'informational',
      metadata: {},
    },
    // ── Avant-hier ──
    {
      id: 'ra-011',
      seniorId: 'andre',
      contactId: 'marie',
      category: 'call',
      action: 'call_ended',
      label: 'Appel audio terminé avec André — 8 minutes',
      description: 'Appel audio sortant',
      createdAt: '2026-07-26T11:30:00-04:00',
      relatedEntityId: 'call-003',
      status: 'informational',
      durationSeconds: 480,
      metadata: { callId: 'call-003', callType: 'audio', direction: 'outgoing' },
    },
    {
      id: 'ra-012',
      seniorId: 'andre',
      category: 'connection',
      action: 'tablet_offline',
      label: 'La tablette d\'André était hors ligne',
      description: 'Interruption de connexion détectée le soir',
      createdAt: '2026-07-26T22:00:00-04:00',
      relatedEntityId: 'notif-tablet-001',
      status: 'handled',
      metadata: { notificationId: 'notif-tablet-001' },
    },
    {
      id: 'ra-013',
      seniorId: 'andre',
      contactId: 'marie',
      category: 'photo',
      action: 'photo_sent',
      label: 'Vous avez envoyé une photo à André',
      description: 'Photo du jardin en fleurs',
      createdAt: '2026-07-26T14:10:00-04:00',
      status: 'informational',
      metadata: { mediaId: 'photo-old-001' },
    },
    {
      id: 'ra-014',
      seniorId: 'andre',
      category: 'radio',
      action: 'radio_listened',
      label: 'André a écouté France Inter',
      description: 'Écoute de la matinale',
      createdAt: '2026-07-26T08:00:00-04:00',
      status: 'informational',
      metadata: { station: 'France Inter' },
    },
  ],
};

// In-memory state for demo (BACKEND INTEGRATION POINT: replace with Zustand/React Context + API)
let currentState: DemoState = JSON.parse(JSON.stringify(INITIAL_STATE));

export function getDemoState(): DemoState {
  return currentState;
}

export function resetDemoState(): void {
  currentState = JSON.parse(JSON.stringify(INITIAL_STATE));
}

export function handleNotification(notifId: string, newStatus: NotificationStatus): void {
  const notif = currentState.familyNotifications.find((n) => n.id === notifId);
  if (notif) {
    notif.status = newStatus;
  }
  // Also update any matching notification message in the conversation
  const msgs = currentState.messagesByContact['andre'] ?? [];
  msgs.forEach((m) => {
    if (m.notifId === notifId && newStatus === 'handled') {
      m.readStatus = 'handled';
    }
  });
  // Sync matching recentActivity (callback) by relatedEntityId = notifId
  const matchingActivity = currentState.recentActivities.find(
    (a) => a.category === 'callback' && a.relatedEntityId === notifId
  );
  if (matchingActivity) {
    if (newStatus === 'handled') {
      matchingActivity.status = 'handled';
    } else if (newStatus === 'deferred') {
      matchingActivity.status = 'deferred';
    } else if (newStatus === 'seen' || newStatus === 'pending') {
      matchingActivity.status = 'pending';
    } else if (newStatus === 'calling') {
      matchingActivity.status = 'calling';
    } else if (newStatus === 'failed') {
      matchingActivity.status = 'failed';
    }
  }
}

export function addAgendaEvent(event: AgendaEvent): void {
  currentState.agendaEvents.push(event);
}

export function updateAgendaEvent(id: string, updates: Partial<AgendaEvent>): void {
  const idx = currentState.agendaEvents.findIndex((e) => e.id === id);
  if (idx !== -1) {
    currentState.agendaEvents[idx] = { ...currentState.agendaEvents[idx], ...updates };
  }
}

export function deleteAgendaEvent(id: string): void {
  currentState.agendaEvents = currentState.agendaEvents.filter((e) => e.id !== id);
}

export function addActivity(activity: FamilyActivity): void {
  // Prevent duplicate activities for the same call session (check by id prefix)
  const exists = currentState.familyActivities.some((a) => a.id === activity.id);
  if (!exists) {
    currentState.familyActivities.unshift(activity);
  }
}

export function addMessage(contactKey: string, message: Message): void {
  if (!currentState.messagesByContact[contactKey]) {
    currentState.messagesByContact[contactKey] = [];
  }
  // Prevent duplicates
  const exists = currentState.messagesByContact[contactKey].some((m) => m.id === message.id);
  if (!exists) {
    currentState.messagesByContact[contactKey].push(message);
  }
}

export function addCallRecord(record: CallRecord): void {
  // Prevent duplicate call records
  const exists = currentState.callHistory.some((c) => c.id === record.id);
  if (!exists) {
    currentState.callHistory.unshift(record);
    // Update seniorProfile last call info
    if (record.status === 'ended' && record.durationSeconds !== null) {
      currentState.seniorProfile.lastCallAt = record.startedAt;
      currentState.seniorProfile.lastCallDuration = formatDurationLabel(record.durationSeconds);
    }
  }
}

export function addRecentActivity(activity: RecentActivity): void {
  const exists = currentState.recentActivities.some((a) => a.id === activity.id);
  if (!exists) {
    currentState.recentActivities.unshift(activity);
  }
}

export function updateRecentActivityStatus(id: string, status: ActivityStatus): void {
  const act = currentState.recentActivities.find((a) => a.id === id);
  if (act) act.status = status;
}

export function updateRecentActivityByNotifId(
  notifId: string,
  updates: Partial<Pick<RecentActivity, 'status' | 'metadata'>>
): void {
  const act = currentState.recentActivities.find(
    (a) => a.category === 'callback' && a.relatedEntityId === notifId
  );
  if (act) {
    if (updates.status !== undefined) act.status = updates.status;
    if (updates.metadata !== undefined) act.metadata = { ...(act.metadata ?? {}), ...updates.metadata };
  }
}

export function getUnreadMessageCount(): number {
  const msgs = currentState.messagesByContact['andre'] ?? [];
  return msgs.filter((m) => m.direction === 'incoming' && m.readStatus !== 'seen' && m.readStatus !== 'handled').length;
}

export function getUnreadAlertCount(): number {
  return currentState.familyNotifications.filter(
    (n) => n.status === 'unread' || n.status === 'deferred'
  ).length;
}

// ─── Family Contacts helpers ─────────────────────────────────────────────────

export function getFamilyContacts(): FamilyContact[] {
  return currentState.familyContacts;
}

export function updateFamilyContact(id: string, updates: Partial<FamilyContact>): void {
  const idx = currentState.familyContacts.findIndex((c) => c.id === id);
  if (idx !== -1) {
    currentState.familyContacts[idx] = { ...currentState.familyContacts[idx], ...updates };
  }
}

export function removeFamilyContact(id: string): void {
  // Never remove admin
  const contact = currentState.familyContacts.find((c) => c.id === id);
  if (!contact || contact.isAdmin) return;
  currentState.familyContacts = currentState.familyContacts.filter((c) => c.id !== id);
}

export function addFamilyContact(contact: FamilyContact): void {
  const exists = currentState.familyContacts.some((c) => c.id === contact.id);
  if (!exists) {
    currentState.familyContacts.push(contact);
  }
}

export function formatDurationLabel(secs: number): string {
  if (secs < 60) return `${secs} seconde${secs > 1 ? 's' : ''}`;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return s > 0 ? `${m} min ${s} sec` : `${m} minute${m > 1 ? 's' : ''}`;
}

export function formatLastCallLabel(isoDate: string): string {
  try {
    const callDate = new Date(isoDate);
    const now = new Date();
    const todayStr = now.toDateString();
    const callStr = callDate.toDateString();
    const h = callDate.getHours().toString().padStart(2, '0');
    const m = callDate.getMinutes().toString().padStart(2, '0');
    if (callStr === todayStr) {
      return `aujourd'hui à ${h}h${m}`;
    }
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (callStr === yesterday.toDateString()) {
      return `hier à ${h}h${m}`;
    }
    return callDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' }) + ` à ${h}h${m}`;
  } catch {
    return 'date inconnue';
  }
}

export { INITIAL_STATE };